const sloganStatus = [
	"init-slogan", // 0
	"pre-active-slogan", // 1
	"active-slogan", // 2
	"post-active-slogan", // 3
	"end-slogan", // 4
];
const angPoses = [120, 121, 119];
let doubtImage;
let angImage;
let slogans;
let currentSlogan;
let alignFrame;
let listSlogans;
let sloganListHeight = 0;
let sloganMetrics = new WeakMap();
const alignmentCache = new Map();
const descriptionGap = 23;

const clearSloganOffsets = () => {
	Array.from(slogans || []).forEach((slogan) => {
		slogan.style.removeProperty("--title-offset-y");
	});
};

const updateSloganStack = (activeOffset = 0) => {
	const isDesktop = window.matchMedia("(min-width: 992px)").matches;
	if (!isDesktop) return;

	const previousSlogan = listSlogans?.querySelector(".pre-active-slogan");
	const activeSlogan = listSlogans?.querySelector(".active-slogan");
	const nextSlogan = listSlogans?.querySelector(".post-active-slogan");
	const previousMetrics = sloganMetrics.get(previousSlogan);
	const activeMetrics = sloganMetrics.get(activeSlogan);
	const nextMetrics = sloganMetrics.get(nextSlogan);

	if (!previousMetrics || !activeMetrics || !nextMetrics) return;

	const previousHeight =
		previousMetrics.titleHeight + descriptionGap +
		previousMetrics.descriptionHeight;
	const activeTop =
		sloganListHeight / 2 + activeOffset - activeMetrics.titleHeight / 2;
	const activeBottom =
		sloganListHeight / 2 + activeOffset + activeMetrics.titleHeight / 2 +
		descriptionGap + activeMetrics.descriptionHeight;
	const previousBaseTop =
		sloganListHeight / 2 - previousMetrics.titleHeight / 2;
	const nextBaseTop = sloganListHeight / 2 - nextMetrics.titleHeight / 2;

	listSlogans.style.setProperty(
		"--pre-slogan-offset-y",
		`${activeTop - descriptionGap - previousHeight - previousBaseTop}px`
	);
	listSlogans.style.setProperty(
		"--post-slogan-offset-y",
		`${activeBottom + descriptionGap - nextBaseTop}px`
	);
};

const measureSloganLayout = () => {
	const isDesktop = window.matchMedia("(min-width: 992px)").matches;

	if (!isDesktop) {
		Array.from(slogans || []).forEach((slogan) => {
			slogan.style.removeProperty("--description-offset-y");
		});
		listSlogans?.style.removeProperty("--pre-slogan-offset-y");
		listSlogans?.style.removeProperty("--post-slogan-offset-y");
		sloganMetrics = new WeakMap();
		return;
	}

	sloganListHeight = listSlogans.getBoundingClientRect().height;
	const measurements = Array.from(slogans || []).map((slogan) => ({
		slogan,
		titleHeight: slogan.querySelector("h3")?.getBoundingClientRect().height || 0,
		descriptionHeight:
			slogan.querySelector("p")?.getBoundingClientRect().height || 0,
	}));

	sloganMetrics = new WeakMap();
	measurements.forEach((measurement) => {
		sloganMetrics.set(measurement.slogan, measurement);
		measurement.slogan.style.setProperty(
			"--description-offset-y",
			`${measurement.titleHeight / 2 + descriptionGap}px`
		);
	});

	updateSloganStack();
};

const getRightPath = (root) => {
	const paths = Array.from(root.querySelectorAll("svg path"));
	if (!paths.length) return null;

	return paths.reduce((current, path) =>
		path.getBoundingClientRect().right > current.getBoundingClientRect().right
			? path
			: current
	);
};

const getVerticalCenter = (element) => {
	const rect = element.getBoundingClientRect();
	return rect.top + rect.height / 2;
};

const getRelativePathCenter = (root) => {
	const rightPath = getRightPath(root);
	if (!rightPath) return null;

	return getVerticalCenter(rightPath) - getVerticalCenter(root);
};

const applySloganOffset = (relativePathCenter) => {
	const activeSlogan = listSlogans?.querySelector(".active-slogan");
	if (!activeSlogan || relativePathCenter === null) return;

	const targetCenter = getVerticalCenter(doubtImage) + relativePathCenter;
	const titleCenter = getVerticalCenter(listSlogans);
	const activeOffset = targetCenter - titleCenter;

	activeSlogan.style.setProperty(
		"--title-offset-y",
		`${activeOffset}px`
	);
	updateSloganStack(activeOffset);
};

const canAlignSlogan = () =>
	window.matchMedia("(min-width: 992px)").matches &&
	doubtImage &&
	listSlogans;

const alignActiveSlogan = () => {
	clearSloganOffsets();
	updateSloganStack();
	if (!canAlignSlogan()) return;

	applySloganOffset(getRelativePathCenter(doubtImage));
};

const alignActiveSloganToAngle = (angle) => {
	clearSloganOffsets();
	updateSloganStack();
	if (!canAlignSlogan()) return;

	const imageWidth = doubtImage.offsetWidth;
	const imageHeight = doubtImage.offsetHeight;
	const normalizedAngle = ((angle % 360) + 360) % 360;
	const cacheKey = `${normalizedAngle}:${imageWidth}x${imageHeight}`;
	const cachedCenter = alignmentCache.get(cacheKey);

	if (cachedCenter !== undefined) {
		applySloganOffset(cachedCenter);
		return;
	}

	const clone = doubtImage.cloneNode(true);
	clone.setAttribute("aria-hidden", "true");
	clone.querySelectorAll("[id]").forEach((element) => {
		element.removeAttribute("id");
	});
	clone.style.cssText = `position:fixed;left:-1000px;top:0;width:${imageWidth}px;height:${imageHeight}px;flex:none;margin:0;visibility:hidden;pointer-events:none;transition:none;transform:rotate(${angle}deg)`;

	const figure = clone.querySelector("figure");
	const svg = clone.querySelector("svg");
	if (figure) {
		figure.style.cssText = `width:${imageWidth}px;height:${imageHeight}px;margin:0`;
	}
	if (svg) {
		svg.style.cssText = `display:block;width:${imageWidth}px;height:${imageHeight}px`;
	}

	doubtImage.parentElement.appendChild(clone);
	const relativePathCenter = getRelativePathCenter(clone);
	clone.remove();

	if (relativePathCenter !== null) {
		alignmentCache.set(cacheKey, relativePathCenter);
		applySloganOffset(relativePathCenter);
	}
};

const scheduleActiveSloganAlignment = () => {
	window.cancelAnimationFrame(alignFrame);
	alignFrame = window.requestAnimationFrame(alignActiveSlogan);
};

const handleSloganResize = () => {
	alignmentCache.clear();
	measureSloganLayout();
	scheduleActiveSloganAlignment();
};

const setInitPosSlogan = () => {
	let initSlogan = (currentSlogan - 2) % slogans.length;
	slogans[initSlogan].classList.remove(sloganStatus[1]);
	slogans[initSlogan].classList.add(sloganStatus[0]);
};

const setPostInitSlogan = () => {
	let postSlogan = (currentSlogan - 1) % slogans.length;
	slogans[postSlogan].classList.remove(sloganStatus[2]);
	slogans[postSlogan].classList.add(sloganStatus[1]);
};

const setCurrentPosSlogan = () => {
	let current = currentSlogan % slogans.length;
	slogans[current].classList.remove(sloganStatus[3]);
	slogans[current].classList.add(sloganStatus[2]);
};

const setPreEndSlogan = () => {
	let preSlogan = (currentSlogan + 1) % slogans.length;
	slogans[preSlogan].classList.remove(sloganStatus[4]);
	slogans[preSlogan].classList.add(sloganStatus[3]);
};

const setEndPosSlogan = () => {
	let endSlogan = (currentSlogan + 2) % slogans.length;
	slogans[endSlogan].classList.remove(sloganStatus[0]);
	slogans[endSlogan].classList.add(sloganStatus[4]);
};

const setImagePos = () => {
	let imgPos = currentSlogan % 3;
	angImage -= angPoses[imgPos];
	doubtImage.style.transform = "rotate(" + angImage + "deg)";
};

const setCurrentSlogan = () => {
	setImagePos();
	setInitPosSlogan();
	setPostInitSlogan();
	setCurrentPosSlogan();
	setPreEndSlogan();
	setEndPosSlogan();
	currentSlogan++;
	alignActiveSloganToAngle(angImage);
};

const doubtsSlide = () => {
	listSlogans = document.querySelector(".list-slogans");
	if (!listSlogans) return;

	slogans = listSlogans.querySelectorAll(".doubt-slogan");
	currentSlogan = slogans.length;
	doubtImage = document.querySelector(".doubt-image");
	angImage = -248;

	if (slogans.length && doubtImage) {
		doubtImage.addEventListener("transitionend", (event) => {
			if (event.propertyName === "transform") {
				scheduleActiveSloganAlignment();
			}
		});
		window.addEventListener("resize", handleSloganResize);
		document.fonts?.ready.then(() => {
			measureSloganLayout();
			scheduleActiveSloganAlignment();
		});
		measureSloganLayout();
		setCurrentSlogan();
		setInterval(setCurrentSlogan, 3000);
	}
};

export { doubtsSlide };
